import { AuthContextProvider, useAuth } from '@teko/auth-react';
import {
  Body,
  ThemeProvider as DSThemeProvider,
  LoadingProvider,
  generateTheme,
  useModal,
} from '@teko/ds-desktop';
import { logger } from '@teko/log';
import { DeviceProvider } from '@teko/teko-ecommerce';
import { ThemeProvider, Theme as UiTheme } from '@teko/ui';
import { default as TerraKit, default as terraKit } from '@terra-js/terra-kit';
import { SmartBanner } from 'components/SmartBanner';
import { LanguageSelector } from 'components/desktop/organisms';
import MetaHead from 'components/mobile/organisms/MetaHead';
import {
  SEARCH_ALGO,
  USER_LOCATION_CODE,
  USER_TERMINAL_CODE,
  USER_TERMINAL_ID,
} from 'constants/index';
import { RegionPickerProvider } from 'contexts/RegionPickerProvider';
import {
  MockStorage,
  getCookieByName,
  getExpireTime,
  getSearchAlgo,
  // getUserSelectedInfo,
  isHomepage,
  isServerless,
  setCookieByName,
  setCookieValue,
  storageAvailable,
} from 'helpers';
import { translateObject } from 'helpers/translate';
import { usePos365StoreCode } from 'hooks';
import { DEFAULT_LANGUAGE, changeLanguage, t } from 'i18n';
import { NotificationProvider } from 'modules';
import NextTracker from 'next-tracker';
import App, { AppContext, NextWebVitalsMetric } from 'next/app';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import Url, { Router, useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';
import { renderTopBarMenu } from 'tenants/_template/desktop/_helpers';
import { parse as parseUrl } from 'url';
import {
  CartProviderV2,
  CategoriesProvider,
  DistanceTerminalProvider,
  FeatureFlagProvider,
  FrameProvider,
  GlobalDataProvider,
  HelperProvider,
  HistoryProvider,
  LanguageProvider,
  LocationProvider,
  LoyaltyProvider,
  PopularKeywordProvider,
  PrivacyPolicyProvider,
  RouterProvider,
  ScreenProvider,
  ServiceProvider,
  ThreeLevelLocationProvider,
  initializeServices,
  useGlobalData,
  usePrivacyPolicy,
  useServices,
} from '../contexts';
import { removePathTrailingSlash } from '../ports/next';
import { TenantEnv } from '../types';
import { ClientTenantEnvImpl } from './_env';
import layouts from './_layouts';
import routesTracking from './_routes';

const ProfileWithAddressMobile = dynamic(() =>
  import('modules/profile/mobile/ProfileWithAddress').then(
    (mod) => mod.ProfileWithAddressMobile,
  ),
);

const ProfileWithAddressDesktop = dynamic(() =>
  import('modules/profile/desktop/ProfileWithAddress').then(
    (mod) => mod.ProfileWithAddressDesktop,
  ),
);

const ProfileUpdateModal = dynamic(() =>
  import('components/desktop/organisms/ProfileUpdateModal').then(
    (mod) => mod.ProfileUpdateModal,
  ),
);

const ProfileFormMobile = dynamic(() =>
  import('components/mobile/organisms/Auth/ProfileForm').then(
    (mod) => mod.ProfileForm,
  ),
);

const serverless = isServerless();

const Layout: React.FC<
  LayoutProps & {
    Component: MyAppProps['Component'];
  }
> = ({ Component, ...props }) => {
  const {
    children,
    banners,
    menuItems,
    liteLogo,
    layoutAreas,
    layoutConfig,
    pageProps,
    inlineBanners,
  } = props;

  const {
    isMobile,
    template,
    tenant,
    footerDataConfig = {},
    rightMenuTopBarButtonConfigs = [],
    leftMenuTopBarButtonConfigs = [],
    availableLanguages,
    requireLogin,
    profileRequiredAfterLogin,
    isDmsFlow,
    supportedPhone,
    isNeedToUpdateProfile,
    forceUpdatePhoneNumber,
  } = useGlobalData();

  const {
    user,
    loading: isLoadingUser,
    showProfileModal,
    setShowProfileModal,
  } = useAuth();
  const router = useRouter();

  const {
    shouldShowSelector: shouldShowPrivacyPolicySelector,
    showSelector: showPrivacyPolicySelector,
  } = usePrivacyPolicy();

  const { orderService } = useServices();

  const { showModal } = useModal();

  // load a language resource on the client
  useEffect(() => {
    if (serverless && window.track) {
      const userId = user?.userId || window.TekoID?.user?.getUserInfo()?.sub;
      if (userId) {
        window.track('setUserId', userId);
      }

      // comment out because link tracking
      // sends duplicate events
      // window.track('enableLinkClick');
    }
  }, [user?.userId, serverless && !!window.TekoID?.user?.getUserInfo()]);

  const showDmsFlowError = (showCloseButton = false) => {
    showModal({
      title: t('Thông báo'),
      children: (
        <Body>
          {t(
            'Không lấy được thông tin để hiển thị sản phẩm dành cho bạn. Vui lòng thử lại hoặc liên hệ {{phone}} để được hỗ trợ',
            { phone: supportedPhone },
          )}
        </Body>
      ),
      okText: t('Thử lại'),
      onOk: () => {
        window.location.reload();
      },
      ...(showCloseButton
        ? {
            cancelText: t('Đóng'),
            onCancel() {
              window?.TekoID?.user?.logout();
            },
          }
        : {
            cancelText: '',
          }),
      closable: false,
      showCloseIcon: false,
      width: 400,
    });
  };

  useEffect(() => {
    if (isDmsFlow && serverless && !isLoadingUser) {
      const currentUserTerminalId = getCookieByName(USER_TERMINAL_ID);
      const currentUserTerminalCode = getCookieByName(USER_TERMINAL_CODE);
      const currentUserLocationCode = getCookieByName(USER_LOCATION_CODE);
      if (user) {
        const loadUserLocationAndTerminal = async () => {
          let needReloadPage = false;
          // if user has not default address => throw error
          if (!user.address) {
            // Display error
            showDmsFlowError(true);
            return;
          } else {
            // set location = detail level location of user
            const userLocation =
              user.address.wardCode ||
              user.address.districtCode ||
              user.address.provinceCode;
            if (!!userLocation && userLocation !== currentUserLocationCode) {
              setCookieValue(USER_LOCATION_CODE, userLocation);
              needReloadPage = true;
            }
          }

          try {
            const customerTypeId = user.customerTypeId;
            if (customerTypeId) {
              const { terminalId, terminalCode } =
                await orderService.getUserTerminal({ customerTypeId });
              if (
                !!terminalId &&
                !!terminalCode &&
                currentUserTerminalId !== terminalId.toString() &&
                currentUserTerminalCode !== terminalCode
              ) {
                setCookieValue(USER_TERMINAL_ID, terminalId.toString());
                setCookieValue(USER_TERMINAL_CODE, terminalCode);
                needReloadPage = true;
              }
            }
          } catch (error) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            switch ((error as any)?.code) {
              case 404:
                // case 404 user unassign terminal => allow user select location
                if (!!currentUserTerminalCode && !!currentUserTerminalId) {
                  setCookieValue(USER_TERMINAL_ID, '');
                  setCookieValue(USER_TERMINAL_CODE, '');
                  needReloadPage = true;
                }
                break;
              default:
                showDmsFlowError();
            }
          } finally {
            if (needReloadPage) {
              window.location.reload();
            }
          }
        };
        loadUserLocationAndTerminal();
      }
    }
  }, [user, isDmsFlow, serverless, isLoadingUser]);

  useEffect(() => {
    // login page has it's own show pivacy policy logic
    if (
      shouldShowPrivacyPolicySelector &&
      !router.pathname.endsWith('/login') &&
      !!user?.userId
    )
      showPrivacyPolicySelector(() => {});
  }, [shouldShowPrivacyPolicySelector]);
  // Trigger show profile modal in case forceUpdatePhoneNumber = false (for SGC)
  useEffect(() => {
    if (
      isNeedToUpdateProfile &&
      !forceUpdatePhoneNumber &&
      user?.userId &&
      !user?.telephone
    ) {
      setShowProfileModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.telephone]);
  // Get showProfileModal info is saved from step confirm account success to show profile modal (for SGC)
  useEffect(() => {
    const value = sessionStorage?.getItem('showProfileModal') === 'true';
    if (value) setShowProfileModal(value);
  }, []);

  //@TODO: remove tenant in here
  const tpl = template || tenant;
  const defaultTemplate = 'vnshop';
  const footerData = translateObject(footerDataConfig);
  const rightMenu = renderTopBarMenu(
    translateObject(rightMenuTopBarButtonConfigs as object),
    !!availableLanguages ? [LanguageSelector] : [],
  );
  const leftMenu = renderTopBarMenu(
    translateObject(leftMenuTopBarButtonConfigs),
  );

  const showProfileWithAddressUpdate =
    profileRequiredAfterLogin &&
    !!user &&
    (!user?.name || !user?.dob || !user?.sex || !user?.address);
  // In case, need to update profile after login (SGC) and user does not have a phone number (showProfileModal state is checked in auth context - after confirm phone number in case login with social)
  const showProfileUpdateModal =
    isNeedToUpdateProfile && !!user && showProfileModal;

  const handleCloseProfileModal = () => {
    setShowProfileModal(false);
    sessionStorage?.removeItem('showProfileModal');
  };

  if (isMobile) {
    const MobileLayout =
      layouts.get(`${tpl}_mobile`) || layouts.get(`${defaultTemplate}_mobile`);

    return showProfileWithAddressUpdate ? (
      <ProfileWithAddressMobile />
    ) : showProfileUpdateModal ? (
      <ProfileFormMobile
        phone={user?.telephone || ''}
        onCloseForm={handleCloseProfileModal}
        profileId={user?.id}
      />
    ) : (
      <MobileLayout
        forceLogin={requireLogin}
        layoutAreas={layoutAreas}
        getLayout={Component?.getLayout}
        layoutConfig={layoutConfig}
        pageProps={pageProps}
        menuItems={menuItems}
        inlineBanners={inlineBanners}
      >
        {children}
      </MobileLayout>
    );
  } else {
    const DesktopLayout =
      layouts.get(`${tpl}_desktop`) ||
      layouts.get(`${defaultTemplate}_desktop`);
    return (
      <div className="minWidthWrapper" style={{ minWidth: '945px' }}>
        <DesktopLayout
          banners={banners}
          menuItems={menuItems}
          liteLogo={liteLogo}
          footerData={footerData}
          rightMenu={rightMenu}
          leftMenu={leftMenu}
          // hotfix the forceLogin for karavan
          // will refractor this in ECOM-362
          forceLogin={requireLogin}
          layoutAreas={layoutAreas}
          layoutConfig={layoutConfig}
          pageProps={pageProps}
          inlineBanners={inlineBanners}
        >
          {children}
          {showProfileWithAddressUpdate && <ProfileWithAddressDesktop />}
          {showProfileUpdateModal && (
            <ProfileUpdateModal
              visible={showProfileModal}
              onCloseModal={handleCloseProfileModal}
              phone={user?.telephone || ''}
              profileId={user?.id}
            />
          )}
        </DesktopLayout>
      </div>
    );
  }
};

const MyApp: React.FunctionComponent<MyAppProps> = ({
  Component,
  pageProps,
  globalData,
  banners,
  menuItems,
  featureFlags,
  liteLogo,
  layoutAreas,
  tempiLayout,
  inlineBanners,
}: MyAppProps) => {
  if (!globalData || Object.keys(globalData).length === 0) {
    // as getInitialProps function now fallback to an empty object
    // we throw exception when object is empty
    logger.error(
      `Empty global data when render ${Component.name}. Probably unable to detect tenant from hostname.`,
    );
    throw new Error('Missing global data when rendering component');
  }
  // alter localStorage and sessionStorage with a simple storage in case storages are not available
  if (typeof window !== 'undefined' && !storageAvailable('localStorage')) {
    Object.defineProperty(window, 'localStorage', {
      value: new MockStorage(),
    });
  }
  if (typeof window !== 'undefined' && !storageAvailable('sessionStorage')) {
    Object.defineProperty(window, 'sessionStorage', {
      value: new MockStorage(),
    });
  }

  // menuItems, banners and layoutAreas (custom page)
  // are fetched in Document.getInitialProps
  // and passed to MyApp in renderPage function.
  // For client-side transition,
  // get them from window object instead.
  banners = banners || (serverless ? window.banners : []);
  menuItems = menuItems || (serverless ? window.menuItems : []);
  layoutAreas = layoutAreas || (serverless ? window.layoutAreas : []);
  tempiLayout = tempiLayout || (serverless ? window.tempiLayout : undefined);
  pageProps = { ...pageProps, menu: tempiLayout?.menu || [] };
  featureFlags = featureFlags || (serverless ? window.featureFlags : null);
  inlineBanners = inlineBanners || (serverless ? window.inlineBanners : null);

  const { publicRuntimeConfig } = getConfig();

  const {
    tenant,
    isMobile,
    scopes,
    terminal,
    currentLanguage,
    trackingId,
    enableSearchAlgoABTesting,
    disableATCFromListingPageRegexes = [],
    // epicOmni1584,
  } = globalData;

  // object fetched by getInitialProps does not
  // preserve function getProcessEnv when comes to client-side
  // re-initialize the object for that

  // if (epicOmni1584) {
  //   const { terminal: terminalCookie, terminalId } =
  //     getTerminalFromCookie(isMobile);
  //   globalData = {
  //     ...globalData,
  //     terminal:
  //       epicOmni1584 &&
  //       !!terminalCookie &&
  //       terminalCookie !== globalData.terminal
  //         ? terminalCookie
  //         : globalData.terminal,
  //     terminalId:
  //       epicOmni1584 &&
  //       !!terminalId &&
  //       Number(terminalId) !== globalData.terminalId
  //         ? Number(terminalId)
  //         : globalData.terminalId,
  //   };
  // }

  if (!globalData.getProcessEnv) {
    globalData = new ClientTenantEnvImpl(globalData);
  }

  const languageCode = currentLanguage || DEFAULT_LANGUAGE;
  if (!serverless) {
    changeLanguage(languageCode);
  }

  const router = useRouter();
  const { query } = router;

  const [history, setHistory] = useState<string[]>([]);

  useEffect(() => {
    setHistory((prev) => {
      if (prev[prev.length - 1] === router.asPath) return prev;
      const newHistory = prev.length >= 20 ? prev.slice(1) : prev;
      return [...newHistory, router.asPath];
    });
  }, [router.asPath]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (router && !(router as any).intercepted) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const customizedRouter = router as any;
    // the singleton router object created
    // intercept call to this method

    // this code ensure that when a next/link is clicked on the client-side
    // and navigation happens, the actual data fetched is prefixed with <tenant> path
    // Under the hood, next/link calls next/router to trigger page navigation
    // and it populates "as" field by default with the same value as "path";
    ['replace', 'push', 'prefetch'].forEach((method) => {
      const originalMethod = customizedRouter[method];

      customizedRouter[method] = (
        path: typeof Url,
        asPath: typeof Url,
        // arguments can be anything
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...args: any
      ) => {
        // turn off client-side routing for websites embedded in apps
        if (featureFlags?.epic_ecom_479 && TerraKit.isAvailable()) {
          if (['replace', 'push'].includes(method)) {
            window.location.href =
              asPath?.toString() || path?.toString() || window.location.href;
          }
          return;
        }

        const { buildId } = window.__NEXT_DATA__;
        const defaultTenant = publicRuntimeConfig.defaultTenantDir;
        const { pathname } = parseUrl(
          typeof path === 'string' ? path : path.pathname,
        );
        if (pathname?.match(/\/([\w\d\-]+)\/(mobile|desktop)(.*)/)) {
          return originalMethod.call(router, path, asPath, ...args);
        }
        const platform = isMobile ? 'mobile' : 'desktop';
        const pages = window.__BUILD_MANIFEST?.sortedPages || [];

        const customizedPath = removePathTrailingSlash(
          `/${tenant}/${platform}${pathname}`,
        );
        const defaultPath = removePathTrailingSlash(
          `/${defaultTenant}/${platform}${pathname}`,
        );
        let finalTenant = '';

        if (buildId === 'development') {
          finalTenant = pages.includes(customizedPath)
            ? tenant
            : pages.includes(defaultPath)
            ? defaultTenant
            : tenant;
        } else {
          finalTenant = pages.includes(customizedPath) ? tenant : defaultTenant;
        }

        const param =
          typeof path === 'string'
            ? removePathTrailingSlash(`/${finalTenant}/${platform}${path}`)
            : {
                ...path,
                pathname: removePathTrailingSlash(
                  `/${finalTenant}/${platform}${path.pathname}`,
                ),
              };

        asPath = asPath || path;
        return originalMethod.call(router, param, asPath, ...args);
      };
    });

    const originalMethod = customizedRouter['back'];

    // method call arguments can be anything
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    customizedRouter['back'] = (...args: any) => {
      if (!history.length) return router.push('/');
      return originalMethod.call(...args);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (router as any).intercepted = true;
  }

  usePos365StoreCode();

  useEffect(() => {
    const initTracker = (trackerAppId?: string) => {
      if (trackerAppId) {
        new NextTracker({
          host: globalData.getProcessEnv('TRACKER_HOST_URL'),
          urlServeJsFile: globalData.getProcessEnv('TRACKER_SERVER_JS_URL'),
          appId: trackerAppId,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          routes: routesTracking as any,
          injectScript: false,
        });
        window.track('enableTrackVisibleContentImpressions');
        window.track('setTerminal', terminal);

        /**
         * because tracking is low priority so they should be
         * executed in idle cycle to avoid blocking user handler
         * TEMPORARILY COMMENT OUT BECAUSE IT CAUSING A BUG
         */
        // const trackOrigin = window.track;
        // window.track = function (...args: any[]) {
        //   function _track(deadline: IdleDeadline) {
        //     if (deadline.timeRemaining() > 0 || deadline.didTimeout) {
        //       if (args.length === 1) {
        //         const [event] = args;
        //         trackOrigin(event);
        //       } else if (args.length === 2) {
        //         const [event, data] = args;
        //         trackOrigin(event, data);
        //       } else {
        //         const [eventType, eventName, data] = args;
        //         trackOrigin(eventType, eventName, data);
        //       }
        //       return;
        //     }
        //     requestIdleCallback(_track);
        //   }
        //   requestIdleCallback(_track);
        // };

        if (serverless) {
          const originalFetch = window.fetch;
          window.fetch = function (...args) {
            return originalFetch
              .apply(this, args)
              .then(async function (data: Response) {
                if (data.status >= 400) {
                  data
                    .clone()
                    .json()
                    .then(
                      (res: {
                        code: string;
                        message: string;
                        error?: { message?: string };
                      }) => {
                        window.track('error', {
                          errorSource: 'http',
                          apiCall: data.url,
                          apiPayload: JSON.stringify(args),
                          httpResponseCode: `${data.status}`,
                          responseJson: JSON.stringify(res),
                          errorCode: `${res.code}` || 'unknown',
                          errorMessage:
                            res.message || res?.error?.message || 'unknown',
                        });
                      },
                    );
                }
                return data;
              });
          };
        }
      } else {
        if (serverless) {
          // polyfill with a dummy tracking function
          // to avoid undefined fn errors somewhere else
          window.track = () => {};
        }
      }
    };
    if (featureFlags?.epic_ecom_479 && terraKit.isAvailable()) {
      terraKit
        .tracker()
        .getTrackingAppId()
        .then((trackerAppId) => initTracker(trackerAppId));
    } else {
      initTracker(globalData.getProcessEnv('TRACKER_APP_ID') || trackingId);
    }

    saveUtmCookie(query);
    if (query?.referral) {
      setCookieByName(
        'referral',
        `${query.referral.toString()};SameSite=None;Secure;`,
      );
    }
  }, []);

  useEffect(() => {
    if (serverless && !!enableSearchAlgoABTesting) {
      // find search algo in local storage
      const localSearchAlgo = getCookieByName(SEARCH_ALGO.COOKIE);
      // if no local search algo version found
      if (!localSearchAlgo && localSearchAlgo !== '') {
        // generate cookie expire time
        const expireTime = getExpireTime(SEARCH_ALGO.EXPIRE_TIME);

        // random a new algo & set to cookie
        setCookieByName(SEARCH_ALGO.COOKIE, getSearchAlgo(), expireTime, '/');
      }

      // override search_algorithm value in cookie if user override in url
      if (
        !!Object.keys(router.query).length &&
        !!router.query.hasOwnProperty(SEARCH_ALGO.COOKIE)
      ) {
        // generate cookie expire time
        const expireTime = getExpireTime(SEARCH_ALGO.EXPIRE_TIME);

        setCookieByName(
          SEARCH_ALGO.COOKIE,
          JSON.stringify(router.query[SEARCH_ALGO.COOKIE]),
          expireTime,
          '/',
        );
      }
    }
  }, [enableSearchAlgoABTesting, router.query]);

  const services = initializeServices(globalData);

  const uiTheme = globalData.theme as UiTheme;
  const dsTheme = useMemo(() => {
    const theme = generateTheme({
      primary: uiTheme.brand.primary,
      secondary: uiTheme.brand.secondary,
    });
    theme.colors = { ...theme.colors, ...uiTheme.colors };
    return theme;
  }, [uiTheme.brand.primary, uiTheme.brand.secondary, uiTheme.colors]);

  const isAllowATCFromListing = disableATCFromListingPageRegexes.every(
    (regex) => !new RegExp(regex).test(router.asPath),
  );
  globalData.isAllowATCFromListing = isAllowATCFromListing;

  return (
    <LanguageProvider code={languageCode}>
      <RouterProvider router={router}>
        <ThemeProvider theme={uiTheme}>
          <DSThemeProvider theme={dsTheme}>
            <LoadingProvider
              logo={(globalData.logo?.loading || globalData.logo?.lite)?.path}
            >
              <MetaHead
                theme={uiTheme}
                config={globalData}
                meta={globalData.meta}
              />
              <GlobalDataProvider value={globalData}>
                <ServiceProvider services={services}>
                  <HelperProvider
                    discoveryPos365Service={services.discoveryPos365Service}
                    freeShippingThreshold={globalData?.freeShippingThreshold}
                  >
                    <AuthContextProvider
                      partner={globalData.partner ?? ''}
                      scopes={
                        scopes || [
                          'openid',
                          'profile',
                          'us',
                          'om',
                          'ppm',
                          'loyalty-consumer-bff',
                          'payment-consumer-bff',
                          'staff-bff',
                        ]
                      }
                      userService={services.userService}
                      authService={services.authService}
                      router={router}
                      showConfirmModalAfterLogin={
                        globalData?.showConfirmModalAfterLogin
                      }
                      forceUpdatePhoneNumber={
                        globalData?.forceUpdatePhoneNumber
                      }
                      isNeedToUpdateProfile={globalData?.isNeedToUpdateProfile}
                      otpRequestCountDown={globalData?.otpRequestCountDown}
                    >
                      <FeatureFlagProvider flags={featureFlags}>
                        <LocationProvider>
                          <FrameProvider>
                            <PopularKeywordProvider>
                              <ScreenProvider>
                                <LoyaltyProvider>
                                  <CartProviderV2>
                                    <NotificationProvider>
                                      <DeviceProvider value={{ isMobile }}>
                                        <PrivacyPolicyProvider>
                                          <ThreeLevelLocationProvider>
                                            <DistanceTerminalProvider>
                                              <RegionPickerProvider>
                                                <CategoriesProvider>
                                                  <Layout
                                                    Component={Component}
                                                    banners={banners}
                                                    menuItems={menuItems}
                                                    liteLogo={liteLogo}
                                                    layoutAreas={layoutAreas}
                                                    layoutConfig={
                                                      tempiLayout?.layoutConfig
                                                    }
                                                    pageProps={pageProps}
                                                    inlineBanners={
                                                      inlineBanners
                                                    }
                                                  >
                                                    <HistoryProvider>
                                                      <SmartBanner />
                                                      <Component
                                                        {...pageProps}
                                                        menuItems={menuItems}
                                                        inlineBanners={
                                                          inlineBanners
                                                        }
                                                      />
                                                    </HistoryProvider>
                                                  </Layout>
                                                </CategoriesProvider>
                                              </RegionPickerProvider>
                                            </DistanceTerminalProvider>
                                          </ThreeLevelLocationProvider>
                                        </PrivacyPolicyProvider>
                                      </DeviceProvider>
                                    </NotificationProvider>
                                  </CartProviderV2>
                                </LoyaltyProvider>
                              </ScreenProvider>
                            </PopularKeywordProvider>
                          </FrameProvider>
                        </LocationProvider>
                      </FeatureFlagProvider>
                    </AuthContextProvider>
                  </HelperProvider>
                </ServiceProvider>
              </GlobalDataProvider>
            </LoadingProvider>
          </DSThemeProvider>
        </ThemeProvider>
      </RouterProvider>
    </LanguageProvider>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(MyApp as any).getInitialProps = async (appContext: AppContext) => {
  // note this function is called every time
  // page transition happen,
  // so please think twice before adding any data-fetching logic
  // here.
  // Ideally, we would like to remove this function altogether.
  // But Nextjs's static optimization does not call
  // Document.getInitialProps for pages like /404,
  // which prevents the page from getting platform config.
  let globalTenantEnv: TenantEnv;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((appContext.ctx.req as any)?.tenantEnv?.tenant === 'sgc') {
    logger.info(appContext.ctx.req, 'appContext.ctx.req');
  }
  if (appContext.ctx.req) {
    // for pages that are SSR
    // this function is triggered on server-side
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    globalTenantEnv = (appContext.ctx.req as any).tenantEnv;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((appContext.ctx.req as any)?.tenantEnv?.tenant === 'sgc') {
      logger.info(globalTenantEnv, 'globalTenantEnv');
    }

    // normally, this should not happen
    // but server log has issues with this otherwise
    // adding log to detect issues
    if (!globalTenantEnv) {
      logger.error(
        'Could not find tenant config for request ' + appContext.ctx.req.url,
      );
    }
  } else {
    // for client-side only page, get this value from window
    // since req object is not set
    //@ts-ignore
    globalTenantEnv = window.__tenantConfig;
    logger.info(globalTenantEnv, 'window.__tenantConfig');
  }

  const liteLogo = !isHomepage(appContext.ctx.asPath ?? '');

  const appProps = await App.getInitialProps(appContext);
  return {
    ...appProps,
    globalData: omitServerFields(globalTenantEnv),
    liteLogo,
  };
};

// omit some fields without using in clients, ex: updatedUrlMap
function omitServerFields(tenantConfig: TenantEnv): Partial<TenantEnv> {
  if (!tenantConfig) {
    return {};
  }

  const serverFields = new Set(['updatedUrlMap']);
  return Object.fromEntries(
    Object.entries(tenantConfig || {}).filter(([k]) => !serverFields.has(k)),
  );
}

export function reportWebVitals(metric: NextWebVitalsMetric) {
  if (!serverless) return;

  if (metric.label === 'web-vital') {
    window.track?.('customEvent', 'web-vital', {
      category: metric.name,
      label: metric.label,
      value: Math.round(
        metric.name === 'CLS' ? metric.value * 1000 : metric.value, // values must be integers
      ),
    });
  }
}

export default MyApp;

const saveUtmCookie = (query: typeof Router.prototype.query) => {
  const {
    utm_source = '',
    utm_campaign = '',
    utm_term = '',
    utm_content = '',
    utm_medium = '',
    gclid = '',
    fbclid = '',
  } = query;

  const currentDate = new Date();
  const date = new Date(
    currentDate.getFullYear() + 2,
    currentDate.getMonth(),
    currentDate.getDay(),
  );
  const expireTime = date.toUTCString();

  if (utm_source) {
    setCookieByName('utmSource', utm_source as string, expireTime);
    setCookieByName('utmCampaign', utm_campaign as string, expireTime);
    setCookieByName('utmTerm', utm_term as string, expireTime);
    setCookieByName('utmContent', utm_content as string, expireTime);
    setCookieByName('utmMedium', utm_medium as string, expireTime);
  }

  if (gclid) setCookieByName('gclid', gclid as string, expireTime);
  if (fbclid) setCookieByName('fbclid', fbclid as string, expireTime);
};

// const getTerminalFromCookie = (
//   isMobile: boolean,
// ): { terminal: string; terminalId: string } => {
//   const terminalCodeFromStorage = getUserSelectedInfo(
//     userSelectedConstants.TERMINAL,
//     isMobile,
//     true,
//   );
//   const terminalIdFromStorage = getUserSelectedInfo(
//     userSelectedConstants.TERMINAL_ID,
//     isMobile,
//     true,
//   );

//   return {
//     terminal: terminalCodeFromStorage || '',
//     terminalId: terminalIdFromStorage || '',
//   };
// };
